import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { AppView, Button, ButtonGroup, CardView, Indicator, NavigationHeader, rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, Spacer, } from 'design-system';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { HEIGHT } from 'design-system/Button';
import FormattedAddress from 'features/invest/components/FormattedAddress';
import useColors from 'hooks/useColors';
import { useUserAddress } from 'hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import RadioButton from 'design-system/RadioButton';
import { StyleSheet, View } from 'react-native';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { format } from 'date-fns';
import { DOB_STORAGE_FORMAT } from 'hooks/useDateTimePickerDOB';
import { OnboardingFlow } from 'utils/types/navigation';
import { useAppDispatch } from 'store/hooks';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
const strings = {
    title: 'Confirm your child’s address',
    subTitle: 'We might ask you to verify these details with a document. Make sure they are correct',
    changeAddress: 'This is not my child’s address',
    continue: 'Continue',
};
const JISAChildAddress = ({ navigation, route }) => {
    const colors = useColors();
    const { primaryAddress } = useUserAddress();
    const scrollY = useSharedValue(0);
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const dispatch = useAppDispatch();
    const navigateToNextScreen = useNavigateToNextScreen();
    const { paddingHorizontalStyle } = useAppFrameDimensions(mountedOnAnotherScreen);
    const contentContainerStyle = useMarginBottom('paddingBottom', 2 * HEIGHT + SCREEN_BOTTOM_PADDING + rem(16), paddingHorizontalStyle);
    const [isAddressCorrect, setIsAddressCorrect] = useState(false);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const changeAddress = useCallback(() => {
        const { firstName, lastName, dob, hasJISAOrTrustFund } = route.params;
        navigation.navigate('AddressPostCodeScreen', {
            flow: OnboardingFlow.JISA,
            childDetails: {
                firstName,
                surname: lastName,
                hasPriorJisa: hasJISAOrTrustFund,
                dateOfBirth: format(dob, DOB_STORAGE_FORMAT),
            },
        });
    }, [route.params]);
    const onContinue = useCallback(() => {
        if (primaryAddress) {
            dispatch({ type: 'SAVE_ONBOARDING_STATE', childAddress: primaryAddress });
            navigateToNextScreen();
        }
    }, [navigateToNextScreen, primaryAddress]);
    const handleConfirmPress = useCallback(() => {
        setIsAddressCorrect((oldValue) => !oldValue);
    }, []);
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y;
    });
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    if (!primaryAddress) {
        return (<AppView>
        <Indicator dark/>
      </AppView>);
    }
    return (<AppView withFrame={false}>
      <NavigationHeader scrollY={scrollY} regularTitleHeight outputColors={headerColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Invest}/> : undefined} rightContent={<View style={!!mountedOnAnotherScreen && styles.rightBtnContainer}>
            <HelpScreenButton />
          </View>}/>
      <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title={strings.title} subTitle={strings.subTitle}/>
        <CardView noMarginTop noMarginBottom withPadding>
          <FormattedAddress {...primaryAddress} uk/>
        </CardView>
        <TouchableBounceCard onDark onPress={handleConfirmPress}>
          <View style={styles.row}>
            <RadioButton type="multiple" isChecked={isAddressCorrect}/>
            <Spacer w={16}/>
            <View style={styles.flex}>
              <Text TextThin-14>I confirm that my child’s address is correct.</Text>
            </View>
          </View>
        </TouchableBounceCard>
      </Animated.ScrollView>
      <ButtonGroup type="floating" isInTab={!!mountedOnAnotherScreen}>
        <Button brandReversed title={strings.changeAddress} onPress={changeAddress}/>
        <Button brand title={strings.continue} onPress={onContinue} disabled={!isAddressCorrect}/>
      </ButtonGroup>
    </AppView>);
};
const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: rem(16),
    },
    flex: {
        flex: 1,
    },
    rightBtnContainer: {
        marginBottom: rem(6),
        marginRight: rem(16),
        alignItems: 'flex-end',
    },
});
export default JISAChildAddress;
